import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Megaphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getTruckingAlert, setTruckingAlert } from "@/lib/api";
import { fmtDate } from "@/components/common";

const META = {
  driver_out: { label: "Driver Out", bar: "bg-[#C24E00] text-white" },
  truck_down: { label: "Truck Down", bar: "bg-[#D50000] text-white" },
  delayed:    { label: "Delayed",    bar: "bg-[#FFD600] text-black" },
};
const OPTIONS = ["driver_out", "truck_down", "delayed"];

export default function DriverAlert({ mode = "full" }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["trucking-alert"], queryFn: getTruckingAlert, refetchInterval: 5000 });
  const alert = data || { status: "all_good", message: "", initials: "" };
  const active = alert.status && alert.status !== "all_good";

  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("driver_out");
  const [message, setMessage] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [saving, setSaving] = useState(false);

  const openEditor = () => {
    setStatus(active ? alert.status : "driver_out");
    setMessage(active ? (alert.message || "") : "");
    setStartDate(active ? (alert.start_date || "") : "");
    setEndDate(active ? (alert.end_date || "") : "");
    setOpen(true);
  };

  const persist = async (payload, msg) => {
    setSaving(true);
    try {
      await setTruckingAlert(payload);
      qc.invalidateQueries({ queryKey: ["trucking-alert"] });
      toast.success(msg);
      setOpen(false);
    } catch {
      toast.error("Couldn't save alert");
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    if (!message.trim()) { toast.error("Add a short message so everyone knows what's going on"); return; }
    persist({ status, message, start_date: startDate, end_date: endDate }, active ? "Alert updated" : "Alert posted");
  };
  const removeAlert = () => persist({ status: "all_good", message: "", start_date: "", end_date: "" }, "Alert cleared");

  const dateRange = alert.start_date
    ? (alert.end_date && alert.end_date !== alert.start_date ? `${fmtDate(alert.start_date)} – ${fmtDate(alert.end_date)}` : fmtDate(alert.start_date))
    : "";

  const meta = META[alert.status] || META.driver_out;

  return (
    <div data-testid="driver-alert" className={mode === "bar" && active ? "mb-4" : ""}>
      {mode !== "button" && active && (
        <div className={`flex flex-wrap items-center gap-3 px-5 py-4 ${meta.bar}`}>
          <AlertTriangle className="w-6 h-6 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-head font-bold uppercase tracking-wide text-lg leading-tight">{meta.label}</div>
            {dateRange && <div className="text-sm font-bold mt-0.5" data-testid="driver-alert-dates">{dateRange}</div>}
            {alert.message && <div className="text-sm opacity-95 mt-0.5" data-testid="driver-alert-message">{alert.message}</div>}
          </div>
          <Button data-testid="driver-alert-edit-btn" onClick={openEditor} variant="outline"
            className="rounded-none border-2 border-current bg-transparent hover:bg-white/20 font-bold uppercase text-xs">
            Update / Clear
          </Button>
        </div>
      )}
      {mode !== "bar" && !active && (
        <Button data-testid="driver-alert-post-btn" onClick={openEditor} variant="outline"
          className="rounded-none border-2 border-gray-900 font-bold uppercase text-xs hover:bg-gray-900 hover:text-white">
          <Megaphone className="w-4 h-4 mr-2" /> Post Driver Alert
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-2 border-gray-900 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl font-bold uppercase tracking-tight">Driver Alert</DialogTitle>
            <DialogDescription>Let anyone booking a pickup or delivery know what's going on.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="driver-alert-status" className="mt-1 rounded-none border-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`driver-alert-opt-${o}`}>{META[o].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Message</Label>
              <Textarea data-testid="driver-alert-msg" rows={3} className="mt-1 rounded-none border-2"
                value={message} placeholder="e.g. Alex out until Thursday · Semi #2 down for repair"
                onChange={(e) => setMessage(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-gray-500">From</Label>
                <Input data-testid="driver-alert-start" type="date" className="mt-1 rounded-none border-2"
                  value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-gray-500">To (optional)</Label>
                <Input data-testid="driver-alert-end" type="date" className="mt-1 rounded-none border-2"
                  value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {active ? (
              <Button data-testid="driver-alert-remove-btn" onClick={removeAlert} disabled={saving} variant="outline"
                className="rounded-none border-2 border-[#D50000] text-[#D50000] hover:bg-red-50 font-bold uppercase">
                Remove Alert
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-none border-2" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="driver-alert-save-btn" onClick={save} disabled={saving}
                className="rounded-none bg-gray-900 hover:bg-gray-700 text-white font-bold uppercase">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {active ? "Update Alert" : "Post Alert"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
